import React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { roundFloat } from "../../../utils/round-float";
import { ItemBonusStat, ItemSlotEnum, SetBonus } from "../../../types/items";
import { useAppTranslation } from "../../../i18n/useAppTranslation";
import LevelBadge from "../../LevelBadge";
import ModifiedStat from "../../ModifiedStat";
import { itemOriginalBonus, StatModifiers } from "../../../utils/statModifiers";

type Props = {
  items?: Array<{
    image: string;
    level: number;
    bonusValue: number;
    item: {
      name: string;
      bonusStat: number;
      setId?: number;
      slot?: ItemSlotEnum;
    };
    setId?: number;
    slot?: ItemSlotEnum;
    statModifiers?: StatModifiers;
  }>;
  setBonuses?: SetBonus[];
  className?: string;
  variant?: "inline" | "slots";
  slotCount?: number;
  startIndex?: number;
  direction?: "row" | "column";
  size?: number | string; // px (number) or CSS width value (e.g., "80%")
};

const ITEM_BONUS_STAT_KEYS: Record<number, string> = {
  0: "health",
  1: "armor",
  3: "healing",
  4: "magicResistance",
  5: "damage",
  6: "range",
};

const ItemsList: React.FC<Props> = ({
  items,
  className,
  setBonuses,
  variant = "inline",
  slotCount = 6,
  startIndex = 0,
  direction = "row",
  size,
}) => {
  const { t } = useAppTranslation(["barracks"]);
  const hasItems = Array.isArray(items) && items.length > 0;
  const getBonusStatLabel = (bonusStat: number | undefined) => {
    const key = ITEM_BONUS_STAT_KEYS[bonusStat || 0];
    if (!key) {
      return t(`barracks:stats.${ItemBonusStat[bonusStat || 0]}`, {
        defaultValue: "",
      });
    }
    return t(`barracks:stats.${key}`);
  };

  if (variant === "slots") {
    const safeItems = (items || []).slice();
    const slotToItem = new Map<number, (typeof safeItems)[number]>();
    safeItems.forEach((it) => {
      const slot = it?.item?.slot ?? it?.slot;
      if (slot) slotToItem.set(Number(slot), it);
    });
    // Visual order: 3 left (Helmet, Chest, Legs), 3 right (Weapon, Gloves, Boots)
    const desiredOrder: number[] = [
      ItemSlotEnum.Helmet,
      ItemSlotEnum.Chest,
      ItemSlotEnum.Legs,
      ItemSlotEnum.Boots,
      ItemSlotEnum.Gloves,
      ItemSlotEnum.Weapon,

    ];
    // Build the visual slots strictly by slot type; if a slot has no item, keep it null
    const slots = desiredOrder
      .slice(startIndex, startIndex + slotCount)
      .map((slot) => slotToItem.get(slot) || null);
    const containerClass =
      direction === "column"
        ? (className || "") +
        " d-flex flex-column align-items-center justify-content-center"
        : (className || "") +
        " row w-100 py-0 align-items-center justify-content-center";
    return (
      <div className={containerClass}>
        {slots.map((slotItem, index) => {
          const isPlaceholder = !slotItem;
          const overlay = isPlaceholder ? (
            <Tooltip className="fs-tiny">
              <div style={{ maxWidth: 280, whiteSpace: "normal" }}>
                {t("barracks:items.emptySlot")}
              </div>
            </Tooltip>
          ) : (
            (() => {
              const resolvedSetId = slotItem?.item?.setId ?? slotItem?.setId;
              const setInfo = setBonuses?.find(
                (s) => s.setId === resolvedSetId
              );
              const isComplete = !!setInfo?.isComplete;
              const tooltipBoxStyle: React.CSSProperties = {
                maxWidth: 320,
                whiteSpace: "normal",
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              };
              const promoHeaderStyle: React.CSSProperties = {
                background: isComplete
                  ? "linear-gradient(90deg, #FFD700 0%, #FF8C00 100%)"
                  : "linear-gradient(90deg, #00E0FF 0%, #5B86E5 100%)",
                color: isComplete ? "#2b1f00" : "#081018",
                padding: "6px 10px",
                borderRadius: 4,
                fontWeight: 700,
                marginBottom: 6,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              };
              const previewSize =
                typeof size === "number" ? Math.max(40, size * 1.6) : 48;
              const previewSrc =
                slotItem?.image ||
                `/assets/images/arena/items/${startIndex + index + 1}.png`;
              const isSetBonusActive = !!setInfo?.isComplete;
              return (
                <Tooltip className="fs-tiny">
                  <div style={tooltipBoxStyle}>
                    <div style={promoHeaderStyle}>
                      <span className="d-flex align-items-center">{setInfo?.setName || slotItem?.item?.name}

                        {slotItem && (
                          <LevelBadge
                            level={slotItem.level}
                            style={{
                              marginBottom:"-2px !important",
                              position: "static",
                            }}
                          />
                        )}
                      </span>
                      {setInfo && (
                        <span style={{ fontWeight: 800, fontSize: 12 }}>
                          {isComplete
                            ? t("barracks:items.complete")
                            : `${setInfo.itemsOwned}/${setInfo.totalItems}`}
                        </span>
                      )}
                    </div>
                    <div
                      style={{ display: "flex", gap: 8, alignItems: "center" }}
                    >
                      <div style={{ flex: 1 }}>
                        <div className="d-flex align-items-center justify-content-center">
                          <img
                            src={previewSrc}
                            alt={slotItem?.item?.name || t("barracks:items.item")}
                            style={{
                              width: previewSize,
                              height: previewSize,
                              objectFit: "contain",
                            }}
                          />
                          <div className="fs-md d-flex flex-column ms-2">
                            <b>{slotItem?.item?.name}</b>
                            <div className="fs-small">
                              (+
                              <b>
                                <ModifiedStat
                                  value={roundFloat(slotItem!.bonusValue, 0)}
                                  original={
                                    itemOriginalBonus(slotItem) === undefined
                                      ? undefined
                                      : roundFloat(itemOriginalBonus(slotItem)!, 0)
                                  }
                                />
                              </b>{" "}
                              {getBonusStatLabel(slotItem!.item.bonusStat)})
                            </div>
                          </div>
                        </div>

                        {setInfo && (
                          <div style={{ marginTop: 6, fontSize: 11 }}>
                            <div>
                              <span className="text-success-light">
                                <b>
                                  {t("barracks:items.setBonus")}: +
                                  {setInfo.bonusPercent}%{" "}
                                </b>
                              </span>
                              {getBonusStatLabel(setInfo.bonusStat)}
                            </div>
                            <div style={{ marginTop: 2 }}>
                              <b>{t("barracks:items.status")}:</b>{" "}
                              {isSetBonusActive
                                ? t("barracks:items.active")
                                : t("barracks:items.inactive")}
                            </div>
                            <div style={{ opacity: 0.9 }}>
                              {setInfo.setDescription}
                            </div>
                            {!isComplete && (
                              <div style={{ marginTop: 4 }}>
                                {t("barracks:items.collectRemaining")}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Tooltip>
              );
            })()
          );

          const resolvedSetId = slotItem?.item?.setId ?? slotItem?.setId;
          const setInfo = setBonuses?.find((s) => s.setId === resolvedSetId);
          const isComplete = !!setInfo?.isComplete;
          let containerStyle: React.CSSProperties | undefined;
          if (slotItem && setInfo) {
            containerStyle = isComplete
              ? {
                filter: "drop-shadow(0 0 16px rgba(255,215,0,0.65))",
                border: "1px solid #FFD700",
                borderRadius: 6,
                backgroundImage:
                  "linear-gradient(125deg, rgba(166, 255, 0, 0.44) 0%, rgb(255, 184, 5) 100%)",
              }
              : {
                filter: "drop-shadow(0 0 10px rgba(0,224,255,0.35))",
                border: "1px solid rgba(0,224,255,0.55)",
                borderRadius: 6,
                backgroundImage:
                  "linear-gradient(125deg, rgba(0, 225, 255, 0.13) 0%, rgba(0, 225, 255, 0.27) 100%)",
              };
          } else if (!isPlaceholder) {
            containerStyle = {
              border: "1px solid rgb(126, 105, 82)",
              borderRadius: 6,
              backgroundColor: "rgba(126, 105, 82, 0.4)",
            };
          }

          const wrapperClass =
            direction === "column"
              ? "my-1 p-0 d-flex justify-content-center"
              : "col-6 my-1 p-0 d-flex " +
              (index % 2 === 1 ? "justify-content-end" : "");
          const frameClipPath =
            "polygon(12% 0, 88% 0, 100% 12%, 100% 88%, 88% 100%, 12% 100%, 0 88%, 0 12%)";
          const baseFrameStyle: React.CSSProperties = {
            clipPath: frameClipPath,
            background: "#3b342c",
            filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.6))",
            boxShadow: "inset 0 0 6px rgba(255,255,255,0.06)",
          };
          const placeholderFrameStyle: React.CSSProperties | undefined =
            isPlaceholder
              ? {
                background:
                  "radial-gradient(100% 100% at 50% 50%, rgba(135, 99, 0,0.12) 0%, rgba(135, 99, 0,0.06) 40%, rgba(59,52,44,1) 100%)",
                border: "1px dashed rgba(135, 99, 0, 0.59)",
                boxShadow:
                  "0 0 10px rgba(135, 99, 0,0.35), inset 0 0 8px rgba(255,255,255,0.05)",
              }
              : undefined;
          const frameSize = typeof size === "number" ? size + 6 : 36;
          const frameWH: React.CSSProperties =
            typeof size === "string"
              ? { width: size, height: size }
              : { width: frameSize, height: frameSize };
          return (
            <div className={wrapperClass} key={index}>
              <OverlayTrigger overlay={overlay}>
                <div
                  className="selection-item-frame"
                  style={{
                    ...baseFrameStyle,
                    ...containerStyle,
                    ...placeholderFrameStyle,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    ...frameWH,
                  }}
                >

                  <img
                    style={{
                      width:
                        typeof size === "string"
                          ? size
                          : typeof size === "number"
                            ? `${size}px`
                            : "90%",
                      filter: isPlaceholder
                        ? "grayscale(1) opacity(0.7)"
                        : undefined,
                    }}
                    src={
                      slotItem?.image ||
                      `/assets/images/items/${startIndex + index + 1}.png`
                    }
                    alt={slotItem?.item?.name || t("barracks:items.emptySlot")}
                  />
                </div>
              </OverlayTrigger>
            </div>
          );
        })}
      </div>
    );
  }

  if (!hasItems) return null;

  return (
    <div className={"d-flex align-items-center " + (className || "")}>
      {items?.map((item, index) => (
        <OverlayTrigger
          key={`${item?.image}-${index}`}
          overlay={(() => {
            const resolvedSetId = item?.item?.setId ?? item?.setId;
            const setInfo = setBonuses?.find((s) => s.setId === resolvedSetId);
            const isComplete = !!setInfo?.isComplete;
            const tooltipBoxStyle: React.CSSProperties = {
              maxWidth: 320,
              whiteSpace: "normal",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
            };
            const promoHeaderStyle: React.CSSProperties = {
              background: isComplete
                ? "linear-gradient(90deg, #FFD700 0%, #FF8C00 100%)"
                : "linear-gradient(90deg, #00E0FF 0%, #5B86E5 100%)",
              color: isComplete ? "#2b1f00" : "#081018",
              padding: "6px 10px",
              borderRadius: 4,
              fontWeight: 700,
              marginBottom: 6,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            };
            return (
              <Tooltip className="fs-tiny">
                <div style={tooltipBoxStyle}>
                  <div style={promoHeaderStyle}>
                    <span>{setInfo?.setName || item?.item?.name}</span>
                    {setInfo && (
                      <span style={{ fontWeight: 800, fontSize: 12 }}>
                        {isComplete
                          ? t("barracks:items.complete")
                          : `${setInfo.itemsOwned}/${setInfo.totalItems}`}
                      </span>
                    )}
                  </div>
                  <div>
                    <div>
                      <b>{item?.item?.name}</b> (+
                      <ModifiedStat
                        value={roundFloat(item.bonusValue)}
                        original={
                          itemOriginalBonus(item) === undefined
                            ? undefined
                            : roundFloat(itemOriginalBonus(item)!)
                        }
                      />{" "}
                      {getBonusStatLabel(item?.item?.bonusStat)})
                    </div>
                    {setInfo && (
                      <div style={{ marginTop: 6 }}>
                        <div>
                          <span className="text-success-light">
                            <b>{t("barracks:items.setBonus")}:</b> +
                            {setInfo.bonusPercent}%{" "}
                          </span>
                          {getBonusStatLabel(setInfo.bonusStat)}
                        </div>
                        <div style={{ opacity: 0.9 }}>
                          {setInfo.setDescription}
                        </div>
                        {!isComplete && (
                          <div style={{ marginTop: 4 }}>
                            {t("barracks:items.collectRemaining")}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Tooltip>
            );
          })()}
        >
          {(() => {
            const resolvedSetId = item?.item?.setId ?? item?.setId;
            const setInfo = setBonuses?.find((s) => s.setId === resolvedSetId);
            const isComplete = !!setInfo?.isComplete;
            const containerStyle: React.CSSProperties | undefined = setInfo
              ? isComplete
                ? {
                  boxShadow: "0 0 16px 3px rgba(255,215,0,0.65)",
                  border: "1px solid #FFD700",
                  borderRadius: 6,
                }
                : {
                  boxShadow: "0 0 10px 2px rgba(0,224,255,0.35)",
                  border: "1px solid rgba(0,224,255,0.55)",
                  borderRadius: 6,
                }
              : undefined;
            return (
              <div
                style={containerStyle}
                className={
                  "p-relative p-1 rounded bg-dark  " + (index !== 0 && "ms-1")
                }
              >
                <img
                  alt=""
                  src={item.image}
                  width={typeof size === "number" ? size : 37}
                  height={typeof size === "number" ? size : 37}
                />
                <div className="item-level1">{item.level}</div>
              </div>
            );
          })()}
        </OverlayTrigger>
      ))}
    </div>
  );
};

export default ItemsList;
